import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Alert,
  Avatar,
  Paper,
  Chip,
  Divider,
  Link,
} from '@mui/material';
import {
  PlayArrow,
  CheckCircle,
  Cancel,
  Person,
  QuestionMark,
  OpenInNew,
} from '@mui/icons-material';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useContext } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';

const AnonymousPlayerCard = ({
  anonymousPlayer,
  players,
  tournamentId,
  userGuess,
  results,
  darkMode = false,
}) => {
  const [selectedPlayer, setSelectedPlayer] = useState(userGuess || '');
  const [saving, setSaving] = useState(false);
  const { currentUser } = useContext(AuthContext);
  const [isUserPossiblyBlocked, setIsUserPossiblyBlocked] = useState(false);

  // Check if the user might be the anonymous player to prevent self-guessing
  useEffect(() => {
    const checkUserAssignment = async () => {
      if (currentUser && anonymousPlayer && anonymousPlayer.playerId) {
        try {
          const playerDoc = await getDoc(
            doc(db, 'tournaments', tournamentId, 'players', anonymousPlayer.playerId)
          );
          if (playerDoc.exists() && playerDoc.data().playerProfile === currentUser.uid) {
            setIsUserPossiblyBlocked(true);
          }
        } catch (error) {
          console.error('Error checking user assignment:', error);
        }
      }
    };

    checkUserAssignment();
  }, [currentUser, anonymousPlayer, tournamentId]);

  const handleGuessSubmit = async () => {
    if (!currentUser) {
      alert('You must be logged in to submit a guess');
      return;
    }

    if (!selectedPlayer) {
      alert('Please select a player first');
      return;
    }

    try {
      setSaving(true);

      // Check if user already has guesses for this tournament
      const userGuessesQuery = query(
        collection(db, 'playerGuesses'),
        where('userId', '==', currentUser.uid),
        where('tournamentId', '==', tournamentId)
      );
      const userGuessesSnapshot = await getDocs(userGuessesQuery);

      if (userGuessesSnapshot.empty) {
        // Create new guesses document
        await addDoc(collection(db, 'playerGuesses'), {
          userId: currentUser.uid,
          tournamentId,
          guesses: [
            {
              anonymousNameId: anonymousPlayer.id,
              guessedPlayerId: selectedPlayer,
            },
          ],
          timestamp: new Date(),
        });
      } else {
        // Update existing guesses
        const guessesDoc = userGuessesSnapshot.docs[0];
        const guessesData = guessesDoc.data();

        // Make sure guesses array exists
        if (!guessesData.guesses) {
          guessesData.guesses = [];
        }

        // Check if this anonymous player already has a guess
        const existingGuessIndex = guessesData.guesses.findIndex(
          (g) => g.anonymousNameId === anonymousPlayer.id
        );

        if (existingGuessIndex >= 0) {
          // Update existing guess
          guessesData.guesses[existingGuessIndex].guessedPlayerId = selectedPlayer;
        } else {
          // Add new guess
          guessesData.guesses.push({
            anonymousNameId: anonymousPlayer.id,
            guessedPlayerId: selectedPlayer,
          });
        }

        // Update the document
        await updateDoc(doc(db, 'playerGuesses', guessesDoc.id), {
          guesses: guessesData.guesses,
          timestamp: new Date(),
        });
      }

      setSaving(false);
    } catch (error) {
      console.error('Error saving guess:', error);
      alert('Error saving your guess. Please try again.');
      setSaving(false);
    }
  };

  const isResultPublished = results?.isPublished;
  const correctPlayerId = isResultPublished
    ? results.correctGuesses?.find((g) => g.anonymousNameId === anonymousPlayer.id)?.actualPlayerId
    : null;
  const isCorrectGuess = correctPlayerId === selectedPlayer;

  const selectedPlayerData = players.find((p) => p.id === selectedPlayer);
  const correctPlayerData = players.find((p) => p.id === correctPlayerId);

  const cardBg = darkMode ? 'rgba(255, 255, 255, 0.08)' : 'white';
  const textColor = darkMode ? 'white' : 'text.primary';
  const secondaryTextColor = darkMode ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary';

  return (
    <Card
      sx={{
        width: { xs: '100%', sm: 300 },
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 3,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'transform 0.2s, box-shadow 0.2s',
        bgcolor: cardBg,
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: 6,
        },
      }}
    >
      <Box
        sx={{
          bgcolor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'primary.main',
          color: 'white',
          py: 1.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" component="div">
          {anonymousPlayer.name}
        </Typography>
        <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
          <QuestionMark />
        </Avatar>
      </Box>

      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {anonymousPlayer.clipUrl && (
          <Box mb={2}>
            <Link
              href={anonymousPlayer.clipUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
            >
              <Paper
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  bgcolor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'action.hover',
                  color: textColor,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: darkMode ? 'rgba(255, 255, 255, 0.15)' : 'action.selected',
                  },
                }}
              >
                <PlayArrow color={darkMode ? 'inherit' : 'primary'} />
                <Typography variant="body2" ml={1} color={secondaryTextColor} fontWeight="medium">
                  Watch mystery clip
                </Typography>
                <OpenInNew sx={{ ml: 1, fontSize: '0.875rem', opacity: 0.7 }} />
              </Paper>
            </Link>
          </Box>
        )}

        {isResultPublished ? (
          <Box sx={{ mt: 1 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                borderColor: isCorrectGuess ? 'success.main' : 'error.main',
                bgcolor: darkMode
                  ? isCorrectGuess
                    ? 'rgba(46, 125, 50, 0.2)'
                    : 'rgba(211, 47, 47, 0.2)'
                  : isCorrectGuess
                    ? 'success.lighter'
                    : 'error.lighter',
              }}
            >
              <Typography
                variant="subtitle2"
                gutterBottom
                fontWeight="bold"
                sx={{ color: textColor }}
              >
                Your guess:
              </Typography>

              {userGuess ? (
                <Box display="flex" alignItems="center" gap={1}>
                  {selectedPlayerData?.imageUrl ? (
                    <Avatar src={selectedPlayerData.imageUrl} sx={{ width: 24, height: 24 }} />
                  ) : (
                    <Avatar sx={{ width: 24, height: 24, bgcolor: 'grey.400' }}>
                      <Person sx={{ fontSize: 16 }} />
                    </Avatar>
                  )}
                  <Typography sx={{ color: textColor }}>
                    {selectedPlayerData?.name || 'Unknown player'}
                  </Typography>
                  {isCorrectGuess ? (
                    <CheckCircle color="success" fontSize="small" />
                  ) : (
                    <Cancel color="error" fontSize="small" />
                  )}
                </Box>
              ) : (
                <Typography sx={{ color: secondaryTextColor }}>
                  You didn't submit a guess
                </Typography>
              )}
            </Paper>

            <Divider
              sx={{ my: 2, borderColor: darkMode ? 'rgba(255, 255, 255, 0.12)' : 'divider' }}
            />

            <Box>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                gutterBottom
                sx={{ color: textColor }}
              >
                {anonymousPlayer.name} is actually:
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                {correctPlayerData?.imageUrl ? (
                  <Avatar src={correctPlayerData.imageUrl} />
                ) : (
                  <Avatar>
                    <Person />
                  </Avatar>
                )}
                <Typography variant="body1" fontWeight="medium" sx={{ color: textColor }}>
                  {correctPlayerData?.name || 'Unknown'}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            {isUserPossiblyBlocked ? (
              <Alert
                severity="info"
                sx={{
                  mt: 2,
                  bgcolor: darkMode ? 'rgba(2, 136, 209, 0.2)' : undefined,
                  color: darkMode ? 'white' : undefined,
                  '.MuiAlert-icon': { color: darkMode ? 'white' : undefined },
                }}
              >
                You cannot guess your own identity.
              </Alert>
            ) : !currentUser ? (
              <Alert
                severity="info"
                sx={{
                  mt: 2,
                  bgcolor: darkMode ? 'rgba(2, 136, 209, 0.2)' : undefined,
                  color: darkMode ? 'white' : undefined,
                  '.MuiAlert-icon': { color: darkMode ? 'white' : undefined },
                }}
              >
                Please log in to submit your guess.
              </Alert>
            ) : (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ color: textColor }}>
                  Who do you think this player is?
                </Typography>
                <FormControl
                  fullWidth
                  sx={{
                    mt: 1,
                    '.MuiFormLabel-root': {
                      color: darkMode ? 'rgba(255, 255, 255, 0.7)' : undefined,
                    },
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: darkMode ? 'rgba(255, 255, 255, 0.23)' : undefined,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: darkMode ? 'rgba(255, 255, 255, 0.5)' : undefined,
                    },
                    '.MuiSelect-select': {
                      color: darkMode ? 'white' : undefined,
                    },
                    '.MuiSvgIcon-root': {
                      color: darkMode ? 'rgba(255, 255, 255, 0.7)' : undefined,
                    },
                  }}
                >
                  <InputLabel id={`player-select-${anonymousPlayer.id}`}>Select Player</InputLabel>
                  <Select
                    labelId={`player-select-${anonymousPlayer.id}`}
                    value={selectedPlayer}
                    label="Select Player"
                    onChange={(e) => setSelectedPlayer(e.target.value)}
                    disabled={saving}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Choose a player</em>
                    </MenuItem>
                    {players.length > 0 ? (
                      players.map((player) => (
                        <MenuItem key={player.id} value={player.id}>
                          <Box display="flex" alignItems="center" gap={1} width="100%">
                            {/* Player avatar */}
                            {player.imageUrl ? (
                              <Avatar src={player.imageUrl} sx={{ width: 24, height: 24 }} />
                            ) : (
                              <Avatar sx={{ width: 24, height: 24, bgcolor: 'grey.400' }}>
                                <Person sx={{ fontSize: 16 }} />
                              </Avatar>
                            )}
                            {/* Player name */}
                            <Typography>{player.name}</Typography>
                          </Box>
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No players found</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}
          </>
        )}
      </CardContent>

      {!isResultPublished && currentUser && !isUserPossiblyBlocked && (
        <CardActions sx={{ p: 2, pt: 0, justifyContent: 'center' }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleGuessSubmit}
            disabled={!selectedPlayer || saving}
            sx={{
              borderRadius: 2,
              bgcolor: darkMode ? 'rgba(255, 255, 255, 0.12)' : undefined,
              color: darkMode ? 'white' : undefined,
              '&:hover': {
                bgcolor: darkMode ? 'rgba(255, 255, 255, 0.2)' : undefined,
              },
            }}
          >
            {userGuess ? 'Update Guess' : 'Submit Guess'}
          </Button>
        </CardActions>
      )}
    </Card>
  );
};

export default AnonymousPlayerCard;
